"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ArrayExpectedValueBuilder = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const design_system_1 = require("@hashintel/design-system");
const material_1 = require("@mui/material");
const lodash_uniqueid_1 = __importDefault(require("lodash.uniqueid"));
const hooks_1 = require("material-ui-popup-state/hooks");
const react_1 = require("react");
const react_hook_form_1 = require("react-hook-form");
const default_expected_value_1 = require("../../../shared/default-expected-value");
const expected_values_options_1 = require("../../../shared/expected-values-options");
const custom_expected_value_selector_1 = require("../shared/custom-expected-value-selector");
const delete_expected_value_modal_1 = require("../shared/delete-expected-value-modal");
const expected_value_badge_1 = require("../shared/expected-value-badge");
const object_expected_value_builder_1 = require("../shared/object-expected-value-builder");
const array_min_max_items_1 = require("./array-expected-value-builder/array-min-max-items");
const dataTypeOptions = [
    ...expected_values_options_1.dataTypeOptions,
    "array",
    "object",
];
const deleteExpectedValueAndChildren = (id, expectedValues) => {
    let newExpectedValues = { ...expectedValues };
    const removedExpectedValue = expectedValues[id];
    if (removedExpectedValue) {
        if (removedExpectedValue.data && "itemIds" in removedExpectedValue.data) {
            for (const childId of removedExpectedValue.data.itemIds) {
                newExpectedValues = deleteExpectedValueAndChildren(childId, newExpectedValues);
            }
        }
        delete newExpectedValues[removedExpectedValue.id];
    }
    return newExpectedValues;
};
const ArrayExpectedValueChild = ({ id, index, onlyChild, firstChild, onDelete }) => {
    const [show, setShow] = (0, react_1.useState)(false);
    const { control } = (0, react_hook_form_1.useFormContext)();
    const arrayChild = (0, react_hook_form_1.useWatch)({
        control,
        name: `flattenedCustomExpectedValueList.${id}`,
    });
    (0, react_1.useEffect)(() => {
        setShow(true);
    }, []);
    if (!arrayChild.data) {
        return null;
    }
    const hasContents = "itemIds" in arrayChild.data && arrayChild.data.itemIds.length;
    const deleteChild = () => {
        if (arrayChild.data?.typeId) {
            onDelete(arrayChild.data.typeId);
        }
    };
    return ((0, jsx_runtime_1.jsx)(material_1.Collapse, { in: show && !arrayChild.animatingOut, timeout: 300, children: (0, jsx_runtime_1.jsx)(material_1.Box, { mb: 1, children: arrayChild.data.typeId === "array" ? (
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            (0, jsx_runtime_1.jsx)(exports.ArrayExpectedValueBuilder, { expectedValueId: id, prefix: firstChild ? "CONTAINING AN" : "OR AN", deleteTooltip: `Delete array${hasContents ? " and its contents" : ""}`, index: index, onDelete: deleteChild })) : arrayChild.data.typeId === "object" ? ((0, jsx_runtime_1.jsx)(object_expected_value_builder_1.ObjectExpectedValueBuilder, { expectedValueId: id, prefix: firstChild ? "CONTAINING A" : "OR A", deleteTooltip: `Delete property object${hasContents ? " and its contents" : ""}`, index: index, onDelete: deleteChild })) : ((0, jsx_runtime_1.jsx)(expected_value_badge_1.ExpectedValueBadge, { typeId: arrayChild.data.typeId, prefix: `${onlyChild ? "CONTAINING" : firstChild ? "CONTAINING EITHER" : "OR"}`, deleteTooltip: "Remove data type", onDelete: deleteChild })) }) }));
};
const ArrayExpectedValueBuilder = ({ expectedValueId, prefix, deleteTooltip, onDelete, index = [] }) => {
    const { getValues, setValue, control } = (0, react_hook_form_1.useFormContext)();
    const [flattenedExpectedValues, editingExpectedValueIndex] = (0, react_hook_form_1.useWatch)({
        control,
        name: [`flattenedCustomExpectedValueList`, `editingExpectedValueIndex`],
    });
    const itemIds = (0, react_hook_form_1.useWatch)({
        control,
        name: `flattenedCustomExpectedValueList.${expectedValueId}.data.itemIds`,
    });
    const [dataTypeCount, propertyObjectCount, arrayCount] = (0, react_1.useMemo)(() => {
        const arrays = itemIds.filter((childId) => flattenedExpectedValues[childId]?.data?.typeId === "array").length;
        const objects = itemIds.filter((childId) => flattenedExpectedValues[childId]?.data?.typeId === "object").length;
        const dataTypes = itemIds.length - arrays - objects;
        return [dataTypes, objects, arrays];
    }, [itemIds, flattenedExpectedValues]);
    const deleteModalPopupState = (0, hooks_1.usePopupState)({
        variant: "popover",
        popupId: `deleteArray-${expectedValueId}`,
    });
    const deleteExpectedValueById = (typeId) => {
        const removedExpectedValue = Object.values(flattenedExpectedValues).find((expectedValue) => expectedValue.parentId === expectedValueId &&
            expectedValue.data?.typeId === typeId);
        if (removedExpectedValue) {
            const removedExpectedValueId = removedExpectedValue.id;
            setValue(`flattenedCustomExpectedValueList.${removedExpectedValueId}`, {
                ...removedExpectedValue,
                animatingOut: true,
            });
            setTimeout(() => {
                setValue(`flattenedCustomExpectedValueList`, deleteExpectedValueAndChildren(removedExpectedValueId, getValues("flattenedCustomExpectedValueList")));
                setValue(`flattenedCustomExpectedValueList.${expectedValueId}.data.itemIds`, itemIds.filter((childId) => childId !== removedExpectedValueId));
            }, 300);
        }
        // trigger popper reposition calculation
        window.dispatchEvent(new Event("resize"));
    };
    const value = (0, react_1.useMemo)(() => itemIds.map((itemId) => flattenedExpectedValues[itemId]?.data?.typeId), [itemIds, flattenedExpectedValues]);
    return ((0, jsx_runtime_1.jsxs)(material_1.Stack, { sx: { mb: 1 }, children: [(0, jsx_runtime_1.jsx)(expected_value_badge_1.ExpectedValueBadge, { typeId: "array", prefix: prefix, deleteTooltip: deleteTooltip, onDelete: () => {
                    if (dataTypeCount + arrayCount + propertyObjectCount > 0) {
                        deleteModalPopupState.open();
                    }
                    else {
                        onDelete?.();
                    }
                }, endNode: (0, jsx_runtime_1.jsx)(array_min_max_items_1.ArrayMinMaxItems, { arrayId: expectedValueId }) }), (0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
                    padding: 1.5,
                    flex: 1,
                    background: ({ palette }) => palette.gray[index.length % 2 !== 0 ? 20 : 10],
                    borderBottomRightRadius: 4,
                    borderBottomLeftRadius: 4,
                    position: "relative",
                }, children: [itemIds.map((itemId, pos) => ((0, jsx_runtime_1.jsx)(ArrayExpectedValueChild, { id: itemId, index: [...index, pos], onDelete: (typeId) => deleteExpectedValueById(typeId), onlyChild: itemIds.length === 1, firstChild: pos === 0 }, itemId))), (0, jsx_runtime_1.jsx)(custom_expected_value_selector_1.CustomExpectedValueSelector, { inputLabel: "Add to array", collapsedWidth: 145, value: value, options: dataTypeOptions, renderOption: (optProps, opt) => {
                            return ((0, jsx_runtime_1.jsxs)(material_1.Box, { component: "li", ...optProps, sx: { py: 1.5, px: 2.25 }, children: [(0, jsx_runtime_1.jsx)(design_system_1.FontAwesomeIcon, { icon: { icon: expected_values_options_1.expectedValuesOptions[opt].icon }, sx: (theme) => ({ color: theme.palette.gray[50] }) }), (0, jsx_runtime_1.jsx)(material_1.Typography, { variant: "smallTextLabels", component: "span", ml: 1.5, color: (theme) => theme.palette.gray[80], children: expected_values_options_1.expectedValuesOptions[opt].title }), (0, jsx_runtime_1.jsx)(design_system_1.Chip, { color: "blue", label: "DATA TYPE", sx: { ml: 1.5 } })] }));
                        }, onChange: (_evt, _data, reason, details) => {
                            const typeId = details?.option;
                            if (typeId) {
                                const allowMultiple = expected_values_options_1.expectedValuesOptions[typeId]?.allowMultiple;
                                const defaultData = (0, default_expected_value_1.getDefaultExpectedValue)(typeId);
                                if (reason === "selectOption" ||
                                    (reason === "removeOption" && allowMultiple)) {
                                    const childId = (0, lodash_uniqueid_1.default)();
                                    setValue(`flattenedCustomExpectedValueList`, {
                                        ...getValues("flattenedCustomExpectedValueList"),
                                        [childId]: {
                                            id: childId,
                                            parentId: expectedValueId,
                                            data: defaultData,
                                        },
                                    });
                                    setValue(`flattenedCustomExpectedValueList.${expectedValueId}.data.itemIds`, [...itemIds, childId]);
                                    // trigger popper reposition calculation
                                    window.dispatchEvent(new Event("resize"));
                                }
                                else if (reason === "removeOption") {
                                    deleteExpectedValueById(typeId);
                                }
                            }
                        } }), (0, jsx_runtime_1.jsx)(delete_expected_value_modal_1.DeleteExpectedValueModal, { expectedValueType: "array", popupState: deleteModalPopupState, editing: editingExpectedValueIndex !== undefined && !index.length, onDelete: onDelete, onClose: () => deleteModalPopupState.close(), dataTypeCount: dataTypeCount, arrayCount: arrayCount, propertyObjectCount: propertyObjectCount })] })] }));
};
exports.ArrayExpectedValueBuilder = ArrayExpectedValueBuilder;
